/**
 * hrmsApi.js — Typed wrappers for every HRMS Dashboard REST endpoint.
 *
 * Each function returns the unwrapped `data` field from the ApiResponse
 * envelope, so callers receive the payload directly:
 *
 *   const summary = await hrmsApi.workforce.getSummary();
 *   // summary.totalHeadcount === 1050
 *
 * Error handling: axios throws on non-2xx status. Callers should wrap
 * in try/catch or use React Query / SWR for automatic retry + loading state.
 */

import api from './api';

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authApi = {
  /**
   * POST /auth/login
   * @param {{ username: string, password: string }} credentials
   * @returns {Promise<AuthResponse>} — { accessToken, refreshToken, expiresIn, user }
   * @throws on failure — err.response.data is { success: false, message,
   *   code? } where code is "ACCOUNT_LOCKED" when locked. On a tracked
   *   bad-credentials failure, err.response.data.data.attemptsRemaining
   *   (number) is proposed — not yet implemented by BE.
   *   x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   */
  login: async (credentials) => {
    const { data } = await api.post('/auth/login', credentials);
    return data.data;
  },

  /**
   * POST /auth/refresh
   * @param {string} refreshToken
   * @returns {Promise<AuthResponse>}
   */
  refresh: async (refreshToken) => {
    const { data } = await api.post('/auth/refresh', { refreshToken });
    return data.data;
  },

  /**
   * POST /auth/logout  (requires valid access token)
   */
  logout: async () => {
    await api.post('/auth/logout');
  },

  /**
   * GET /auth/me  — re-hydrate user profile on page refresh
   * @returns {Promise<UserInfo>}
   */
  me: async () => {
    const { data } = await api.get('/auth/me');
    return data.data;
  },

  /**
   * POST /auth/change-password  (self-service, requires current password)
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   * @param {string} currentPassword
   * @param {string} newPassword
   */
  changePassword: async (currentPassword, newPassword) => {
    const { data } = await api.post('/auth/change-password', { currentPassword, newPassword });
    return data.data;
  },

  /**
   * POST /auth/force-password-change
   * Used only when the current session has user.mustChangePassword=true
   * (i.e. right after logging in with an admin-issued temporary password).
   * No currentPassword needed — the user just authenticated with it.
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   * @param {string} newPassword
   */
  forcePasswordChange: async (newPassword) => {
    const { data } = await api.post('/auth/force-password-change', { newPassword });
    return data.data;
  },
};

// ── Tab A — Workforce Pulse ───────────────────────────────────────────────────
export const workforceApi = {
  /**
   * GET /hr/workforce/summary
   * Full payload for WorkforcePulseTab — all KPIs + all chart data.
   * @returns {Promise<WorkforceDto>}
   */
  getSummary: async () => {
    const { data } = await api.get('/hr/workforce/summary');
    return data.data;
  },

  /**
   * GET /hr/workforce/headcount
   * Lightweight KPI poll — totalHeadcount, attritionRate, topHiringDept only.
   */
  getHeadcount: async () => {
    const { data } = await api.get('/hr/workforce/headcount');
    return data.data;
  },

  /**
   * GET /hr/workforce/attrition
   * Attrition trend + forecast series only (for chart refresh).
   */
  getAttritionTrend: async () => {
    const { data } = await api.get('/hr/workforce/attrition');
    return data.data;
  },

  /**
   * GET /hr/workforce/diversity
   * Diversity pie-chart slices only.
   */
  getDiversity: async () => {
    const { data } = await api.get('/hr/workforce/diversity');
    return data.data;
  },

  /**
   * POST /hr/workforce/simulate  (x-status: proposed)
   * Run a what-if workforce scenario and return a projection dataset.
   * @param {{ scenario: string, params: object, horizonMonths: number }} payload
   * @returns {Promise<SimulationResponse>}
   */
  simulate: async (payload) => {
    const { data } = await api.post('/hr/workforce/simulate', payload);
    return data.data;
  },

  /** GET /hr/workforce/attrition-heatmap */
  getAttritionHeatmap: async () => {
    const { data } = await api.get('/hr/workforce/attrition-heatmap');
    return data.data;
  },
};

// ── Tab B — Finance & Budget ──────────────────────────────────────────────────
export const financeApi = {
  /**
   * GET /hr/finance/payroll
   * Full payload for FinanceBudgetTab.
   * @returns {Promise<FinanceDto>}
   */
  getSummary: async () => {
    const { data } = await api.get('/hr/finance/payroll');
    return data.data;
  },

  /** GET /hr/finance/wages — KPI cards only */
  getWagesKpi: async () => {
    const { data } = await api.get('/hr/finance/wages');
    return data.data;
  },

  /** GET /hr/finance/claims — bar chart data */
  getClaims: async () => {
    const { data } = await api.get('/hr/finance/claims');
    return data.data;
  },

  /** GET /hr/finance/earning-funnel — funnel data */
  getEarningFunnel: async () => {
    const { data } = await api.get('/hr/finance/earning-funnel');
    return data.data;
  },

  /** GET /hr/finance/budget-monthly — line chart data */
  getBudgetMonthly: async () => {
    const { data } = await api.get('/hr/finance/budget-monthly');
    return data.data;
  },

  /** GET /hr/finance/budget-forecast — variance + 12-month forecast */
  getBudgetForecast: async () => {
    const { data } = await api.get('/hr/finance/budget-forecast');
    return data.data;
  },
};

// ── Tab C — Operations ────────────────────────────────────────────────────────
export const attendanceApi = {
  /**
   * GET /hr/attendance/summary
   * Full payload for OperationsTab.
   * @returns {Promise<AttendanceDto>}
   */
  getSummary: async () => {
    const { data } = await api.get('/hr/attendance/summary');
    return data.data;
  },

  /**
   * GET /hr/attendance/today
   * Traffic light KPI — presentPct, trafficLight ("green"|"yellow"|"red").
   * Poll every 60 s.
   */
  getToday: async () => {
    const { data } = await api.get('/hr/attendance/today');
    return data.data;
  },

  /** GET /hr/attendance/leave-breakdown */
  getLeaveBreakdown: async () => {
    const { data } = await api.get('/hr/attendance/leave-breakdown');
    return data.data;
  },

  /** GET /hr/attendance/lateness — heatmap data */
  getLatenessHeatmap: async () => {
    const { data } = await api.get('/hr/attendance/lateness');
    return data.data;
  },

  /** GET /hr/attendance/early-leavers — top-5 table */
  getEarlyLeavers: async () => {
    const { data } = await api.get('/hr/attendance/early-leavers');
    return data.data;
  },

  /** GET /hr/attendance/weekly — bar chart */
  getWeekly: async () => {
    const { data } = await api.get('/hr/attendance/weekly');
    return data.data;
  },

  /** GET /hr/attendance/leave-patterns */
  getLeavePatterns: async () => {
    const { data } = await api.get('/hr/attendance/leave-patterns');
    return data.data;
  },
};

// ── Tab D — Talent & Performance ──────────────────────────────────────────────
export const talentApi = {
  /**
   * GET /hr/talent/performance
   * Full payload for TalentPerformanceTab.
   * @returns {Promise<TalentDto>}
   */
  getSummary: async () => {
    const { data } = await api.get('/hr/talent/performance');
    return data.data;
  },

  /** GET /hr/talent/kpi — KPI cards only */
  getKpi: async () => {
    const { data } = await api.get('/hr/talent/kpi');
    return data.data;
  },

  /** GET /hr/talent/bell-curve */
  getBellCurve: async () => {
    const { data } = await api.get('/hr/talent/bell-curve');
    return data.data;
  },

  /** GET /hr/talent/nine-box */
  getNineBox: async () => {
    const { data } = await api.get('/hr/talent/nine-box');
    return data.data;
  },

  /**
   * GET /hr/talent/skill-gap?dept={dept}  (x-status: proposed)
   * @param {string} [dept] — department name; omitted defaults server-side to "IT"
   * @returns {Promise<{ dept: string, asOfDate: string, points: {skill:string, required:number, actual:number}[] }>}
   */
  getSkillGap: async (dept) => {
    const { data } = await api.get('/hr/talent/skill-gap', { params: dept ? { dept } : {} });
    return data.data;
  },

  /**
   * GET /hr/talent/skill-gap/departments  (x-status: proposed)
   * @returns {Promise<{ departments: string[] }>}
   */
  getSkillGapDepartments: async () => {
    const { data } = await api.get('/hr/talent/skill-gap/departments');
    return data.data;
  },

  /** GET /hr/talent/training-demand */
  getTrainingDemand: async () => {
    const { data } = await api.get('/hr/talent/training-demand');
    return data.data;
  },

  /** GET /hr/talent/training-recommendations */
  getRecommendations: async () => {
    const { data } = await api.get('/hr/talent/training-recommendations');
    return data.data;
  },
};


// ── AI Summary Bar ────────────────────────────────────────────────────────────
export const aiApi = {
  /**
   * GET /hr/ai-summary
   * REST fallback — called once on mount; WebSocket pushes updates after.
   * @returns {Promise<{ text: string, generatedAt: number }>}
   */
  getSummary: async () => {
    const { data } = await api.get('/hr/ai-summary');
    return data.data;
  },
  getTabSummary: async (tabKey) => {
    const { data } = await api.get(`/hr/ai-tab-summary?tab=${tabKey}`);
    return data.data;
  },
};

// ── Admin / Settings ──────────────────────────────────────────────────────────
export const adminApi = {
  /**
   * GET /admin/users
   * All users with roles + derived accessibleTabs.
   */
  getUsers: async () => {
    const { data } = await api.get('/admin/users');
    return data.data;
  },

  /**
   * GET /admin/users/:id
   */
  getUser: async (id) => {
    const { data } = await api.get(`/admin/users/${id}`);
    return data.data;
  },

  /**
   * PUT /admin/users/:id/roles
   * @param {number} id
   * @param {string[]} roles — full set of role names to assign
   */
  updateUserRoles: async (id, roles) => {
    const { data } = await api.put(`/admin/users/${id}/roles`, { roles });
    return data.data;
  },

  /**
   * POST /admin/users
   * Create a new user.
   * @param {{ username, password, fullName, email, department, roles }} payload
   */
  createUser: async (payload) => {
    const { data } = await api.post('/admin/users', payload);
    return data.data;
  },

  /**
   * PUT /admin/users/:id/status
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   * @param {number} id
   * @param {boolean} enabled
   */
  updateUserStatus: async (id, enabled) => {
    const { data } = await api.put(`/admin/users/${id}/status`, { enabled });
    return data.data;
  },

  /**
   * PUT /admin/users/:id/profile
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   * @param {number} id
   * @param {{ fullName, email, department }} payload
   */
  updateUserProfile: async (id, payload) => {
    const { data } = await api.put(`/admin/users/${id}/profile`, payload);
    return data.data;
  },

  /**
   * POST /admin/users/:id/reset-password
   * Generates a random temporary password server-side, forces the target
   * user to change it on next login. Returns the plaintext temp password
   * once — the admin must relay it securely (not persisted client-side).
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   * @param {number} id
   * @returns {Promise<{ temporaryPassword: string }>}
   */
  resetUserPassword: async (id) => {
    const { data } = await api.post(`/admin/users/${id}/reset-password`);
    return data.data;
  },

  /**
   * GET /admin/audit-log?userId&action&page&size
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   * @param {{ userId?: number, action?: string, page?: number, size?: number }} params
   * @returns {Promise<{ content: Array, totalElements: number, totalPages: number, page: number, size: number }>}
   */
  getAuditLog: async (params = {}) => {
    const { data } = await api.get('/admin/audit-log', { params });
    return data.data;
  },

  /**
   * PUT /admin/users/:id/unlock
   * Clears accountLocked/failedLoginAttempts/lockedUntil for a locked-out user.
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   * @param {number} id
   */
  unlockUser: async (id) => {
    const { data } = await api.put(`/admin/users/${id}/unlock`);
    return data.data;
  },

  /**
   * GET /admin/roles
   * All available role definitions.
   */
  getRoles: async () => {
    const { data } = await api.get('/admin/roles');
    return data.data;
  },
};

// ── Phase 4: AI Chatbot ──────────────────────────────────────────────────────
export const chatApi = {
  sendMessage: async (sessionId, message) => {
    const { data } = await api.post('/hr/chat/message', { sessionId, message });
    return data.data;
  },
  getHistory: async (sessionId) => {
    const { data } = await api.get(`/hr/chat/history/${sessionId}`);
    return data.data;
  },
  deleteSession: async (sessionId) => {
    await api.delete(`/hr/chat/session/${sessionId}`);
  },
};

// ── Phase 5A: Knowledge Base Admin API ────────────────────────────────────────
export const kbApi = {
  /**
   * POST /admin/kb/ingest  (multipart/form-data)
   * Uploads and ingests a .txt or .md file into the knowledge base.
   * @param {FormData} formData  — fields: file, title, category, roleScope, version
   * @returns {{ documentId, filename, title, chunkCount }}
   */
  ingestDocument: async (formData) => {
    const { data } = await api.post('/admin/kb/ingest', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.data;
  },

  /**
   * GET /admin/kb/documents
   * Lists all knowledge base documents with metadata.
   * @returns {Array<{ id, title, filename, category, roleScope, version, chunkCount, ingestedAt, isActive }>}
   */
  listDocuments: async () => {
    const { data } = await api.get('/admin/kb/documents');
    return data.data;
  },

  /**
   * DELETE /admin/kb/documents/{id}
   * Soft-deletes a document (sets is_active = false).
   */
  deactivateDocument: async (id) => {
    await api.delete(`/admin/kb/documents/${id}`);
  },

  /**
   * DELETE /admin/kb/documents/{id}/purge
   * Permanently deletes an already-inactive document (and its chunks).
   * x-status: proposed — see ../hrms-contracts/api/openapi.yaml
   */
  purgeDocument: async (id) => {
    await api.delete(`/admin/kb/documents/${id}/purge`);
  },

  /**
   * POST /admin/kb/test-search
   * Tests semantic search without going through the chatbot.
   * @param {string} query
   * @param {string} roleScope
   * @returns {Array<{ documentTitle, category, similarity, contentPreview }>}
   */
  testSearch: async (query, roleScope = 'all') => {
    const { data } = await api.post('/admin/kb/test-search', { query, roleScope });
    return data.data;
  },
};

// ── Anomaly Alerts ────────────────────────────────────────────────────────────
export const alertsApi = {
  getAlerts: async () => {
    const { data } = await api.get('/hr/alerts/anomalies');
    return data.data;
  },
  acknowledgeAlert: async (id) => {
    const { data } = await api.put(`/hr/alerts/${id}/acknowledge`);
    return data.data;
  },
};